import { useState } from 'react';
import { ThumbsDown, ThumbsUp, SmilePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  usePostReaction,
  useUserReactions,
  useVideoReactions,
} from '@/hooks/useReactions';
import type { VideoData } from '@/lib/videoTypes';
import { cn } from '@/lib/utils';

const QUICK_EMOJIS = ['❤️', '🔥', '😂', '😮', '😢', '👏', '🫡', '⚡'];

export function VideoReactions({ video }: { video: VideoData }) {
  const { user } = useCurrentUser();
  const counts = useVideoReactions(video);
  const userReactions = useUserReactions(video);
  const postReaction = usePostReaction(video);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [shortcode, setShortcode] = useState('');
  const [emojiUrl, setEmojiUrl] = useState('');

  const liked = userReactions.data?.liked ?? false;
  const disliked = userReactions.data?.disliked ?? false;

  const react = (content: string, emoji?: { shortcode: string; url: string }) => {
    if (!user || postReaction.isPending) return;
    postReaction.mutate({ content, emoji });
    setPickerOpen(false);
  };

  const reactCustomEmoji = () => {
    const code = shortcode.trim().replace(/^:+|:+$/g, '');
    if (!code || !emojiUrl.trim()) return;
    try {
      new URL(emojiUrl.trim());
    } catch {
      return;
    }
    react(`:${code}:`, { shortcode: code, url: emojiUrl.trim() });
    setShortcode('');
    setEmojiUrl('');
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Like / dislike */}
      <div className="flex items-center overflow-hidden rounded-full bg-secondary">
        <Button
          variant="ghost"
          size="sm"
          className={cn('gap-1.5 rounded-none px-3.5', liked && 'text-primary')}
          onClick={() => react('+')}
          disabled={!user}
          title={user ? 'Like' : 'Log in to react'}
        >
          <ThumbsUp className={cn('h-4 w-4', liked && 'fill-current')} />
          <span className="tabular-nums text-sm">{counts.data?.likes ?? 0}</span>
        </Button>
        <div className="h-5 w-px bg-border" />
        <Button
          variant="ghost"
          size="sm"
          className={cn('gap-1.5 rounded-none px-3.5', disliked && 'text-primary')}
          onClick={() => react('-')}
          disabled={!user}
          title={user ? 'Dislike' : 'Log in to react'}
        >
          <ThumbsDown className={cn('h-4 w-4', disliked && 'fill-current')} />
          <span className="tabular-nums text-sm">{counts.data?.dislikes ?? 0}</span>
        </Button>
      </div>

      {/* Custom emoji reactions (NIP-30) */}
      {counts.data?.custom.map((emoji) => (
        <Button
          key={emoji.url ? `${emoji.value}|${emoji.url}` : emoji.value}
          variant="secondary"
          size="sm"
          className={cn(
            'gap-1.5 rounded-full px-3',
            userReactions.data?.custom.includes(emoji.value) && 'ring-1 ring-primary',
          )}
          onClick={() => {
            if (emoji.url) {
              const code = emoji.value.replace(/^:+|:+$/g, '');
              react(emoji.value, { shortcode: code, url: emoji.url });
            } else {
              react(emoji.value);
            }
          }}
          disabled={!user}
        >
          {emoji.url ? (
            <img src={emoji.url} alt={emoji.value} className="h-4 w-4 object-contain" loading="lazy" />
          ) : (
            <span>{emoji.value}</span>
          )}
          <span className="tabular-nums text-xs">{emoji.count}</span>
        </Button>
      ))}

      {/* Emoji picker */}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            className="gap-1.5 rounded-full"
            disabled={!user}
            title={user ? 'Add a custom reaction' : 'Log in to react'}
          >
            <SmilePlus className="h-4 w-4" />
            <span className="hidden sm:inline text-xs">React</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72" align="start">
          <div className="space-y-3">
            <div className="grid grid-cols-8 gap-1">
              {QUICK_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="rounded-md p-1.5 text-lg hover:bg-accent transition-colors"
                  onClick={() => react(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <div className="border-t border-border pt-3 space-y-2">
              <Label className="text-xs text-muted-foreground">
                Custom emoji (NIP-30)
              </Label>
              <Input
                placeholder="shortcode (e.g. blobcat)"
                value={shortcode}
                onChange={(e) => setShortcode(e.target.value)}
                className="h-8 text-sm"
              />
              <Input
                placeholder="https://… image URL"
                value={emojiUrl}
                onChange={(e) => setEmojiUrl(e.target.value)}
                className="h-8 text-sm"
              />
              <Button
                size="sm"
                className="w-full"
                onClick={reactCustomEmoji}
                disabled={!shortcode.trim() || !emojiUrl.trim()}
              >
                React with custom emoji
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
